import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { mockCompanies } from '../mock/data';
import { 
  Tags, Plus, Search, Edit2, Trash2, Building2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

const Sectors = () => {
  const { currentColorTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [editingSector, setEditingSector] = useState(null);

  // Get unique sectors from companies
  const sectors = [...new Set(mockCompanies.map(c => c.industry))];

  // Get company count per sector
  const getSectorStats = (sector) => {
    const companies = mockCompanies.filter(c => c.industry === sector);
    return {
      companyCount: companies.length,
      companies: companies
    };
  };

  // Filter sectors by search
  const filteredSectors = sectors.filter(sector =>
    sector.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Colors for sectors
  const sectorColors = [
    'from-blue-500 to-blue-600',
    'from-purple-500 to-purple-600',
    'from-pink-500 to-pink-600',
    'from-green-500 to-green-600',
    'from-orange-500 to-orange-600',
    'from-cyan-500 to-cyan-600',
  ];

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} -mx-8 -mt-8 p-8`}>
        <h1 className="text-3xl font-bold text-white">Sectors</h1>
        <p className="text-white/80">Manage industry sectors for your companies</p>
      </div>
      {/* Glowing separator line */}
      <div className="-mx-8">
        <HeaderGlow />
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 mt-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search sectors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 w-[280px]"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border border-border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              Table
            </Button>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                Add Sector
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px] bg-card">
              <DialogHeader>
                <DialogTitle className="text-foreground">Add New Sector</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-foreground">Sector Name *</Label>
                  <Input placeholder="e.g., Healthcare, Education..." className="mt-1" />
                </div>
                <div>
                  <Label className="text-foreground">Description</Label>
                  <Input placeholder="Brief description of this sector" className="mt-1" />
                </div>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  onClick={() => {
                    alert('Sector creation will be implemented with backend');
                    setCreateDialogOpen(false);
                  }}
                >
                  Add Sector
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Sectors Grid or Table */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSectors.map((sector, index) => {
            const stats = getSectorStats(sector);
            const colorClass = sectorColors[index % sectorColors.length];
            
            return (
              <Card 
                key={sector} 
                className="bg-card border-border hover:shadow-lg transition-all group overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${colorClass}`} />
                
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                        <Tags className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground text-lg">{sector}</h3>
                        <p className="text-sm text-muted-foreground">
                          {stats.companyCount} {stats.companyCount === 1 ? 'company' : 'companies'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Companies in this sector */}
                  <div className="space-y-2 mb-4">
                    {stats.companies.slice(0, 3).map(company => (
                      <div key={company.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="w-3 h-3" />
                        <span>{company.name}</span>
                      </div>
                    ))}
                    {stats.companies.length > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{stats.companies.length - 3} more
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-border">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditingSector(sector)}>
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Sector</th>
                  <th className="text-center p-4 font-medium text-muted-foreground">Companies</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Top Companies</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSectors.map((sector, index) => {
                  const stats = getSectorStats(sector);
                  const colorClass = sectorColors[index % sectorColors.length];
                  return (
                    <tr key={sector} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                            <Tags className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium text-foreground">{sector}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="secondary">{stats.companyCount}</Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {stats.companies.slice(0, 2).map(c => c.name).join(', ')}
                        {stats.companies.length > 2 && ` +${stats.companies.length - 2} more`}
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditingSector(sector)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {filteredSectors.length === 0 && (
        <div className="text-center py-16">
          <Tags className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No sectors found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Get started by adding your first sector'}
          </p>
        </div>
      )}

      {/* Edit Sector Dialog */}
      <Dialog open={!!editingSector} onOpenChange={(open) => !open && setEditingSector(null)}>
        <DialogContent className="sm:max-w-[400px] bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Edit Sector</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-foreground">Sector Name</Label>
              <Input defaultValue={editingSector} className="mt-1" />
            </div>
            <div>
              <Label className="text-foreground">Description</Label>
              <Input placeholder="Brief description of this sector" className="mt-1" />
            </div>
            <Button 
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => {
                alert('Sector update will be implemented with backend');
                setEditingSector(null);
              }}
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sectors;
